import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RichText from "@/components/RichText";
import VideoPlayer from "@/components/VideoPlayer";
import { publicUrl } from "@/lib/storage";

type Story = {
  id: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  cover_image_path: string | null;
  thumbnail_url: string | null;
  video_url: string | null;
  author: string | null;
  tags: string[] | null;
  status: string;
  published_at: string | null;
};

const MediaStory = () => {
  const { slug } = useParams<{ slug: string }>();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // RLS only returns unpublished rows to admins, so drafts stay admin-only.
      const { data } = await supabase
        .from("media_posts")
        .select(
          "id, title, subtitle, body, cover_image_path, thumbnail_url, video_url, author, tags, status, published_at",
        )
        .eq("slug", slug ?? "")
        .maybeSingle();
      setStory((data as any) ?? null);
      setLoading(false);
    })();
  }, [slug]);

  const hero = publicUrl(story?.cover_image_path ?? null) ?? story?.thumbnail_url ?? null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <div className="container py-10 md:py-16 max-w-3xl">
          <Link to="/media-hub" className="text-xs font-semibold uppercase tracking-[0.2em] text-primary hover:underline">
            ← Media Hub
          </Link>

          {loading ? (
            <p className="text-muted-foreground mt-8">Loading…</p>
          ) : !story ? (
            <p className="text-muted-foreground mt-8">This story isn't available.</p>
          ) : (
            <article className="mt-6">
              {story.status !== "published" && (
                <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-sm font-semibold">
                  {story.status === "preview" ? "Preview" : "Draft"} — only visible to admins.
                </p>
              )}
              <h1 className="text-4xl md:text-6xl text-balance">{story.title}</h1>
              {story.subtitle && <p className="text-lg text-muted-foreground mt-4">{story.subtitle}</p>}
              <p className="text-xs text-muted-foreground mt-4">
                {story.author ? `By ${story.author}` : "Say It In Sports"}
                {story.published_at
                  ? ` · ${new Date(story.published_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}`
                  : ""}
              </p>

              {story.video_url ? (
                <div className="mt-8 aspect-video rounded-2xl overflow-hidden bg-muted">
                  <VideoPlayer url={story.video_url} />
                </div>
              ) : hero ? (
                <img src={hero} alt="" className="mt-8 w-full rounded-2xl aspect-video object-cover" />
              ) : null}

              {story.body && <RichText html={story.body} className="mt-8" />}

              {story.tags && story.tags.length > 0 && (
                <ul className="flex flex-wrap gap-2 mt-8" aria-label="Tags">
                  {story.tags.map((t) => (
                    <li key={t} className="px-3 py-1 rounded-full bg-muted text-xs font-semibold">
                      {t}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MediaStory;
